import { localized, msg } from "@lit/localize";
import { Task } from "@lit/task";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { createElement, X } from "lucide";

import { baseStyles } from "@/elements/assets/css/base";
import { BookingElementController } from "@/elements/booking-element/booking-element.controller";
import { getLocale } from "@/elements/localization/localization.utilities";
import { makePortalWebUnitSelectionUrl } from "@/elements/portal-web/portal-web.utilities";
import { WidgetElementsBookingElementDialogEnterSide } from "@/elements/widget/elements/booking-element/dialog/widget-elements-booking-element-dialog";
import { WidgetController } from "@/elements/widget/widget.controller";

@customElement("bloom-booking-dialog")
@localized()
export class BookingDialogElement extends LitElement {
  private widgetController = new WidgetController(this);
  private bookingElementController = new BookingElementController(this);

  @property({ attribute: "widget-id" })
  private widgetId: string;

  private dialogElement = createRef<HTMLDivElement>();
  private closeButtonElement = createRef<HTMLButtonElement>();
  private iframeElement = createRef<HTMLIFrameElement>();

  private wasOpen = false;
  private openingElement: HTMLElement | null = null;

  private iframeLocale = getLocale();

  private initializeTask = new Task(this, {
    task: async ([widgetId]) => {
      await this.widgetController.initialize(widgetId);
    },
    args: () => [this.widgetId],
  });

  private get iframeSrc(): string {
    return makePortalWebUnitSelectionUrl(
      this.widgetId,
      this.bookingElementController.activePropertyId,
      this.iframeLocale,
    );
  }

  private get closeIcon(): SVGElement {
    const closeIcon = createElement(X);

    closeIcon.setAttribute("height", "20");
    closeIcon.setAttribute("width", "20");

    return closeIcon;
  }

  private get enterSide(): WidgetElementsBookingElementDialogEnterSide {
    return (
      this.bookingElementController.dialogEnterSide ??
      this.widgetController.bookingElement.dialog.enterSide
    );
  }

  private get dialogPositionClasses(): string {
    return `md:group-aria-hidden:translate-x-0 ${
      this.enterSide === WidgetElementsBookingElementDialogEnterSide.Left
        ? "md:right-full md:translate-x-[calc(100%+theme('spacing.3'))]"
        : "md:left-full md:-translate-x-[calc(100%+theme('spacing.3'))]"
    }`;
  }

  private get closeButtonPositionClasses(): string {
    return this.enterSide === WidgetElementsBookingElementDialogEnterSide.Left
      ? "md:left-full md:right-auto md:ml-3"
      : "md:left-auto md:right-full md:mr-3";
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.bookingElementController.closeDialog();
    }

    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (this.shadowRoot?.activeElement === this.dialogElement.value) {
          this.closeButtonElement.value?.focus();
          event.preventDefault();
        }
      } else if (
        this.shadowRoot?.activeElement === this.closeButtonElement.value
      ) {
        this.dialogElement.value?.focus();
        event.preventDefault();
      }
    }
  }

  private handleDialogToggle() {
    if (!this.wasOpen && this.bookingElementController.isDialogOpen) {
      this.handleDialogOpen();
    } else if (this.wasOpen && !this.bookingElementController.isDialogOpen) {
      this.handleDialogClose();
    }

    this.wasOpen = this.bookingElementController.isDialogOpen;
  }

  private handleDialogOpen() {
    if (document.activeElement instanceof HTMLElement) {
      this.openingElement = document.activeElement;
    }

    this.dialogElement.value?.focus();
  }

  private handleDialogClose() {
    this.openingElement?.focus();
  }

  protected override willUpdate() {
    if (!this.bookingElementController.isDialogOpen) {
      this.iframeLocale = getLocale();
    }
  }

  protected override updated() {
    this.handleDialogToggle();
  }

  protected override render() {
    return html`<div
      data-test-id="bloom-booking-dialog"
      aria-hidden="${!this.bookingElementController.isDialogOpen}"
      class="group fixed inset-0 z-[99999] max-h-[100vh] max-w-[100vw] aria-hidden:invisible aria-hidden:transition-[visibility] aria-hidden:delay-500"
    >
      <div
        class="absolute inset-0 bg-black/40 transition-opacity duration-500 group-aria-hidden:opacity-0"
        aria-hidden="true"
        @click="${() => {
          this.bookingElementController.closeDialog();
        }}"
      ></div>

      ${this.initializeTask.render({
        complete: () =>
          html`<div
            ${ref(this.dialogElement)}
            @keydown=${(event: KeyboardEvent) => {
              this.handleKeydown(event);
            }}
            tabindex="0"
            role="dialog"
            aria-modal="true"
            aria-label="${msg("Booking System")}"
            class="${this
              .dialogPositionClasses} absolute bottom-0 h-[90%] w-full translate-y-0 rounded-lg transition-transform duration-500 ease-in-out focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-slate-800 group-aria-hidden:translate-y-full md:inset-y-3 md:h-auto md:w-1/2 md:group-aria-hidden:translate-y-0 xl:w-1/3"
          >
            <iframe
              ${ref(this.iframeElement)}
              allow="payment"
              title="${msg("Booking System")}"
              class="h-full w-full rounded-t-lg bg-white shadow-md md:rounded-lg"
              src="${this.iframeSrc}"
            ></iframe>

            <button
              ${ref(this.closeButtonElement)}
              @click="${() => {
                this.bookingElementController.closeDialog();
              }}"
              aria-label="${msg("close")}"
              class="${this
                .closeButtonPositionClasses} absolute bottom-full left-0 right-0 mx-auto mb-3 w-fit rounded-full bg-white p-2 text-zinc-800 shadow-md transition-opacity duration-500 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white group-aria-hidden:opacity-0 md:bottom-0 md:top-0 md:my-auto md:h-fit"
            >
              ${this.closeIcon}
            </button>
          </div>`,
        error: console.error,
      })}
    </div>`;
  }

  public static override styles = [baseStyles];
}
